'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import Link from 'next/link';
import Image from 'next/image';
import * as gtag from '@/lib/analytics/gtag'; // Import gtag functions
import { Switch } from '@/components/ui/switch';

// Define a function to handle Meta Pixel consent
const updateMetaConsent = (granted: boolean) => {
  if (typeof window === 'undefined') {
    return;
  }

  // If Meta Pixel exists, update its consent
  if (window.fbq) {
    if (granted) {
      // Enable Meta Pixel tracking
      window.fbq('consent', 'grant');
    } else {
      // Disable Meta Pixel tracking
      window.fbq('consent', 'revoke');
    }
  }
};

// Define the consent preferences type
export interface ConsentPreferences {
  necessary: boolean; // Always required
  experience: boolean;
  measurement: boolean;
  marketing: boolean;
  healthData: boolean; // Special category for health data
  timestamp: string;
  version: string;
}

// Default preferences (all checked by default)
export const defaultPreferences: ConsentPreferences = {
  necessary: true,
  experience: true,
  measurement: true,
  marketing: true,
  healthData: true,
  timestamp: new Date().toISOString(),
  version: '1.0'
};

// Helper to check if cookies are not yet accepted
export const cookiesNotAccepted = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  // Check both cookie and localStorage
  const cookieAccepted = document.cookie.includes('cookiePreferences=');
  const localStorageAccepted = !!localStorage.getItem('cookieConsent');
  return !cookieAccepted && !localStorageAccepted;
};

// Helper to get consent data
export const getConsentData = (): ConsentPreferences | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  const match = document.cookie.match(/cookiePreferences=([^;]+)/);
  if (!match) {
    return null;
  }
  try {
    return JSON.parse(decodeURIComponent(match[1]));
  } catch (e) {
    console.error('Error parsing cookie preferences:', e);
    return null;
  }
};

/**
 * Renders a bottom banner cookie consent modal with granular controls.
 */
export default function CookieModal() {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState<ConsentPreferences>(defaultPreferences);
  const t = useTranslations('cookie-modal');

  // Initial check on mount
  useEffect(() => {
    if (cookiesNotAccepted()) {
      setOpen(true);
    }

    // Safety: force cookie check on any click
    const handleClick = () => {
      if (open && !cookiesNotAccepted()) {
        setOpen(false);
      }
    };
    document.addEventListener('click', handleClick);

    // Cleanup
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [open]);

  // Force close if cookies accepted
  useEffect(() => {
    if (!cookiesNotAccepted() && open) {
      setOpen(false);
    }
  }, [open]);
  const storeConsent = (prefs: ConsentPreferences) => {
    const expirationDate = new Date();
    expirationDate.setFullYear(expirationDate.getFullYear() + 1);

    // More aggressive cookie setting
    const prefsJson = JSON.stringify(prefs);
    document.cookie = `cookiePreferences=${encodeURIComponent(prefsJson)}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax`;
    localStorage.setItem('cookieConsent', 'accepted');

    // Force cookie to take effect immediately
    document.cookie = 'testCookie=force; path=/; SameSite=Lax';

    // Update Google consent state
    if (typeof window !== 'undefined' && window.gtag) {
      gtag.updateConsentState({
        analyticsStorage: prefs.measurement ? 'granted' : 'denied',
        adStorage: prefs.marketing ? 'granted' : 'denied',
        adUserData: prefs.marketing ? 'granted' : 'denied',
        adPersonalization: prefs.marketing ? 'granted' : 'denied'
      });
    }

    // Update Meta Pixel consent
    updateMetaConsent(prefs.marketing);
  };
  const handleAcceptAll = () => {
    const allAccepted: ConsentPreferences = {
      ...defaultPreferences,
      experience: true,
      measurement: true,
      marketing: true,
      healthData: true,
      timestamp: new Date().toISOString()
    };
    storeConsent(allAccepted);
    setOpen(false);
  };
  const handleRejectAll = () => {
    // Only necessary cookies are accepted
    storeConsent(defaultPreferences);
    setOpen(false);
  };
  const handleSavePreferences = () => {
    // Save current preferences
    storeConsent({
      ...preferences,
      timestamp: new Date().toISOString()
    });
    // Close the modal completely, not just returning to main screen
    setOpen(false);
  };
  if (!open) {
    return null;
  }
  return <>
      {!showCustomize ?
    // Simple bottom banner
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-offblack border-t border-gray-800 text-white rounded-t-2xl shadow-2xl">
          <div className="container mx-auto px-4 py-4">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pb-4 md:pb-0">
              <div className="flex-1">
                <p className="text-sm text-white">{t('description')}</p>
              </div>
              <div className="flex gap-2 shrink-0 justify-between w-full md:w-auto md:justify-end">
                <Button type="button" variant="outline" size="default" onClick={() => setShowCustomize(true)} className="bg-transparent text-white border-white hover:bg-gray-800 hover:text-white px-4 py-3">
                  {t('personalizza')}
                </Button>
                <Button type="button" variant="default" size="default" className="bg-white text-black hover:bg-gray-200 px-3 py-3" onClick={handleAcceptAll}>
                  {t('consenti-tutti-cookie')}
                </Button>
              </div>
            </div>
          </div>
        </div> :
    // Expanded preferences panel - still at bottom but taller
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-offblack border-t border-gray-800 text-white max-h-[80vh] overflow-hidden flex flex-col rounded-t-lg shadow-2xl">
          <div className="p-3 border-b border-gray-800 sticky top-0 bg-offblack z-10">
            <div className="flex justify-between items-center">
              <p className="text-sm font-medium text-white">{t('cookie-preferences')}</p>
            </div>
            <p className="text-xs text-gray-300 mt-1">
              {t('privacy-policy-text')}{' '}
              <Link href="/legal/privacy-policy" className="text-offblue hover:text-white hover:cursor-pointer">
                {t('privacy-policy')}
              </Link>
              .
            </p>
          </div>

          <div className="flex-1 overflow-y-auto px-4 md:px-6 space-y-3 pb-4">
            {/* Necessary cookies - always enabled */}
            <div className="py-2 border-b border-white">
              <div className="flex justify-between items-center mb-1">
                <p className="text-white text-sm font-medium">{t('strictly-necessary')}</p>
                <Switch checked={true} disabled={true} className="data-[state=checked]:bg-offblue" />
              </div>
              <p className="text-xs text-gray-300">{t('strictly-necessary-description-1')}</p>
            </div>

            {/* Experience cookies */}
            <div className="py-2 border-b border-white">
              <div className="flex justify-between items-center mb-1">
                <p className="text-white text-sm font-medium">{t('preference-cookies')}</p>
                <Switch checked={preferences.experience} onCheckedChange={checked => setPreferences({
              ...preferences,
              experience: checked
            })} className="data-[state=checked]:bg-offblue" />
              </div>
              <p className="text-xs text-gray-300">{t('preference-description-1')}</p>
            </div>

            {/* Measurement cookies */}
            <div className="py-2 border-b border-white">
              <div className="flex justify-between items-center mb-1">
                <p className="text-white text-sm font-medium">{t('analytics-cookies')}</p>
                <Switch checked={preferences.measurement} onCheckedChange={checked => setPreferences({
              ...preferences,
              measurement: checked
            })} className="data-[state=checked]:bg-offblue" />
              </div>
              <p className="text-xs text-gray-300">{t('measurement-description')}</p>
            </div>

            {/* Marketing cookies */}
            <div className="py-2 border-b border-white">
              <div className="flex justify-between items-center mb-1">
                <p className="text-white text-sm font-medium">{t('marketing')}</p>
                <Switch checked={preferences.marketing} onCheckedChange={checked => setPreferences({
              ...preferences,
              marketing: checked
            })} className="data-[state=checked]:bg-offblue" />
              </div>
              <p className="text-xs text-gray-300">{t('marketing-description')}</p>
            </div>

            {/* Health data tracking - special consent */}
            <div className="py-2">
              <div className="flex justify-between items-center mb-1">
                <p className="text-white text-sm font-medium">{t('health-data')}</p>
                <Switch checked={preferences.healthData} onCheckedChange={checked => setPreferences({
              ...preferences,
              healthData: checked
            })} className="data-[state=checked]:bg-offblue" />
              </div>
              <p className="text-xs text-gray-300">{t('health-data-description')}</p>
            </div>
          </div>

          <div className="p-4 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center bg-offblack sticky bottom-0 w-full z-10">
            <button className="text-xs text-offblue hover:text-white order-last md:order-first hover:cursor-pointer mb-2 md:mb-0" onClick={() => setShowCustomize(false)}>
              {t('hide-details')}
            </button>
            <div className="flex flex-col md:flex-row md:gap-2 items-center justify-end flex-1 space-y-2 md:space-y-0 w-full">
              <Button type="button" variant="outline" size="sm" onClick={handleSavePreferences} className="bg-transparent text-white border-white hover:bg-gray-800 text-xs hover:text-white w-full md:w-auto py-2">
                {t('allow-selected-cookies')}
              </Button>
              <Button type="button" variant="default" size="sm" onClick={handleAcceptAll} className="bg-white text-black hover:bg-gray-300 text-xs w-full md:w-auto py-2">
                {t('consenti-tutti-cookie')}
              </Button>
            </div>
          </div>
        </div>}
    </>;
}